import React from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';

interface RangeInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  step?: number;
  unit?: string;
  className?: string;
}

export const RangeInput: React.FC<RangeInputProps> = ({
  label,
  value,
  min,
  max,
  onChange,
  step = 1,
  unit = '',
  className = ''
}) => {
  const { isMobile } = useBreakpoint();

  // MOBILE: Buttony zamiast slidera (dla zakresów 0-10)
  if (isMobile && max <= 10) {
    const options = [];
    for (let i = min; i <= max; i += step) {
      options.push(i);
    }

    return (
      <div className={className} style={{ marginBottom: 'var(--space-4)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-3)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--gray-700)'
        }}>
          {label}
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(50px, 1fr))`,
          gap: 'var(--space-2)',
          maxWidth: '100%'
        }}>
          {options.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              style={{
                padding: 'var(--space-3)',
                minHeight: 'var(--touch-target-min)',
                background: value === option
                  ? 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)'
                  : 'white',
                color: value === option ? 'white' : 'var(--gray-700)',
                border: `2px solid ${value === option ? 'var(--primary-500)' : 'var(--gray-200)'}`,
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-base)',
                fontWeight: value === option ? 'var(--font-semibold)' : 'var(--font-medium)',
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                boxShadow: value === option ? 'var(--shadow-sm)' : 'none'
              }}
            >
              {option}{unit}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // DESKTOP/TABLET: Slider (lub mobile dla zakresów > 10)
  const range = max - min;
  const fillPct = range > 0 ? ((value - min) / range) * 100 : 100;

  return (
    <div className={className} style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-2)'
      }}>
        <label style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--gray-700)'
        }}>
          {label}
        </label>
        <span style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--primary-500)',
          minWidth: '60px',
          textAlign: 'right'
        }}>
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%',
          height: '8px',
          borderRadius: 'var(--radius-full)',
          outline: 'none',
          background: `linear-gradient(to right, var(--primary-500) 0%, var(--primary-500) ${fillPct}%, var(--gray-200) ${fillPct}%, var(--gray-200) 100%)`,
          cursor: 'pointer',
          WebkitAppearance: 'none',
          appearance: 'none'
        }}
      />
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 'var(--space-1)',
        fontSize: 'var(--text-xs)',
        color: 'var(--gray-500)'
      }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};
